// Facility documents — gated route wrapper (legacy
// facility-documents-page-client.tsx). Document management requires the
// `facility.docs.templates` feature (Professional+ / Enterprise); other plans
// see the teaser card. The SPA's FeatureAccessProvider is not mounted
// globally yet, so mount it locally around the gated content — same pattern
// as provider/team.tsx. Backend still enforces the real role check on every
// request; this is UX gating only.

import { useMemo } from "react";

import { Trans } from "@lingui/react/macro";
import { createFileRoute } from "@tanstack/react-router";
import { FileText } from "lucide-react";

import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import { FacilityDocumentsClient } from "@/components/account/facility/facility-documents-client";
import { FacilityFeatureTeaser } from "@/components/account/facility/facility-feature-teaser";
import {
  FeatureAccessProvider,
  useFeatureAccess,
  useFeatureAccessLoading,
} from "@/components/features/feature-access-provider";
import { Skeleton } from "@/components/ui/skeleton";
import type { OwnerType } from "@/lib/features/types";

export const Route = createFileRoute("/_authenticated/account/facility/$facilityId/documents")({
  component: FacilityDocumentsRoute,
});

// Stable reference — feeds FeatureAccessProvider's memoized scope key.
const DOCUMENTS_PAGE_SCOPES: OwnerType[] = ["user", "facility"];

function FacilityDocumentsRoute() {
  const { facilityId } = Route.useParams();

  // The route param IS the facility owner id — pass it explicitly so access
  // resolution doesn't have to guess from the newest active subscription.
  const ownerIds = useMemo(() => ({ facility: facilityId }), [facilityId]);

  return (
    <FeatureAccessProvider activeScopes={DOCUMENTS_PAGE_SCOPES} ownerIds={ownerIds}>
      <div className="space-y-5">
        <GatedFacilityDocuments facilityId={facilityId} />
        <AccountSecurityBanner />
      </div>
    </FeatureAccessProvider>
  );
}

function GatedFacilityDocuments({ facilityId }: { facilityId: string }) {
  const access = useFeatureAccess("facility.docs.templates");
  const loading = useFeatureAccessLoading();

  if (loading || !access) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 w-full rounded-xl" />
      </div>
    );
  }

  // Same allow rule as the legacy page wrapper.
  const allowed =
    access.access === "included" || (access.access === "limited" && !access.exhausted);

  if (!allowed) return <DocumentsFeatureTeaser facilityId={facilityId} />;

  return <FacilityDocumentsClient facilityId={facilityId} />;
}

// ─── Feature teaser (plan without document management) ──────────────────────
//
// Only the document list + search exist today: upload, templates and the AI
// features are marked "coming soon" (founder rule 2026-10-10).

function DocumentsFeatureTeaser({ facilityId }: { facilityId: string }) {
  return (
    <FacilityFeatureTeaser
      facilityId={facilityId}
      pageTitle={<Trans>Documents</Trans>}
      icon={FileText}
      title={<Trans>Document management for your clinic</Trans>}
      bullets={[
        { label: <Trans>Find any document by patient, file name or type</Trans> },
        {
          label: <Trans>Upload patient documents (contracts, consents, lab results)</Trans>,
          soon: true,
        },
        {
          label: <Trans>Reusable templates (GDPR consents, treatment contracts)</Trans>,
          soon: true,
        },
        { label: <Trans>AI text extraction from PDF / images</Trans>, soon: true },
        { label: <Trans>AI summary of patient medical history</Trans>, soon: true },
      ]}
    />
  );
}
